"use client";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Slider from "react-slick";
import api from "../../services/api";

const NewsDetails = () => {
  const { slug } = useParams();

  const [news, setNews] = useState(null);
  const [otherEvents, setOtherEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const leftColRef = useRef(null);
  const rightColRef = useRef(null);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/news-events/${slug}`);
        setNews(res.data.news);
        setOtherEvents(res.data.other_events || []);
      } catch (err) {
        console.error("Failed to load news details", err);
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 300);
      }
    };

    fetchDetails();
  }, [slug]);

  useEffect(() => {
    if (loading || !rightColRef.current) {
      if (rightColRef.current) rightColRef.current.style.height = 'auto';
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (window.innerWidth >= 992 && rightColRef.current) {
          const newHeight = entry.contentRect.height;
          rightColRef.current.style.height = `${newHeight}px`;
        } else if (rightColRef.current) {
          rightColRef.current.style.height = "auto";
        }
      }
    });

    if (leftColRef.current) {
      resizeObserver.observe(leftColRef.current);
    }

    const handleWindowResize = () => {
      if (leftColRef.current && rightColRef.current) {
        if (window.innerWidth < 992) {
          rightColRef.current.style.height = "auto";
        } else {
          rightColRef.current.style.height = `${leftColRef.current.offsetHeight}px`;
        }
      }
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [loading, news, slug]);

  const NextArrow = ({ className, style, onClick }) => (
    <button className={className} style={{ ...style }} onClick={onClick}>
      <i className="fa-solid fa-angle-right"></i>
    </button>
  );

  const PrevArrow = ({ className, style, onClick }) => (
    <button className={className} style={{ ...style }} onClick={onClick}>
      <i className="fa-solid fa-angle-left"></i>
    </button>
  );

  const sliderSettings = {
    customPaging: (i) => (
      <a href="void(0)">
        <img
          src={`${process.env.REACT_APP_WEB_URL}${news?.carousel_images?.[i]}`}
          className="thumb-img img-fluid"
          alt="thumb"
        />
      </a>
    ),
    dots: true,
    dotsClass: "slick-dots slick-thumb",
    arrows: true,
    infinite: true,
    speed: 500,
    fade: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });

  const sidebarNews = React.useMemo(() => {
    if (!news) return [];

    const merged = [
      {
        id: news.id,
        title: news.title,
        slug: news.slug,
        event_date: news.event_date,
        thumbnail: news.thumbnail
      },
      ...otherEvents
    ];

    const uniqueBySlug = Array.from(
      new Map(merged.map(item => [item.slug, item])).values()
    );

    return uniqueBySlug.sort(
      (a, b) => new Date(b.event_date) - new Date(a.event_date)
    );
  }, [news, otherEvents]);

  const NewsDetailsSkeleton = () => (
    <section className="news-details-section">
      <div className="container">
        <div className="row">

          <div className="col-lg-9">
            <div className="skeleton shimmer" style={{ height: "520px", borderRadius: "12px" }}></div>

            <div className="mt-4 skeleton">
              <div className="skeleton-line long shimmer"></div>
              <div className="skeleton-line medium shimmer"></div>

              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="skeleton-line long shimmer"></div>
              ))}
            </div>
          </div>

          <div className="col-lg-3">
            <div className="skeleton">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="d-flex gap-3 mb-3">
                  <div
                    className="skeleton shimmer"
                    style={{ width: "80px", height: "60px", borderRadius: "8px" }}
                  ></div>

                  <div style={{ flex: 1 }}>
                    <div className="skeleton-line short shimmer"></div>
                    <div className="skeleton-line medium shimmer"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );

  if (loading) return <NewsDetailsSkeleton />;

  return (
    <section className="news-details-section">
      <div className="container">
        <div className="row">

          <div className="col-lg-9" ref={leftColRef}>
            <div className="news-details-slider-wrapper">
              <Slider {...sliderSettings} key={slug}>
                {news.carousel_images?.map((img, i) => (
                  <div key={i}>
                    <img
                      src={`${process.env.REACT_APP_WEB_URL}${img}`}
                      className="img-fluid w-100 main-img"
                      alt={news.title}
                    />
                  </div>
                ))}
              </Slider>
            </div>

            <div className="news-details-content mt-4">
              <h2 className="section-title">{news.title}</h2>

              {news.subtitle && (
                <p className="section-subtitle text-center">
                  {news.subtitle}
                </p>
              )}

              <p dangerouslySetInnerHTML={{ __html: news.content }}>
              </p>
            </div>
          </div>

          <div className="col-lg-3" ref={rightColRef}>
            <div className="news-details-sidebar d-flex justify-content-center flex-lg-row flex-column">
              <h2 className="section-title text-start d-block d-lg-none">
                Latest News
              </h2>

              <div className="sidebar-scroll">
                {sidebarNews.map((item) => (
                  <a
                    key={item.id}
                    href={`/news-details/${item.slug}`}
                    className={`sidebar-news-item ${item.slug === slug ? "active" : ""
                      }`}
                    style={{
                      cursor: item.slug === slug ? "default" : "pointer",
                      pointerEvents: item.slug === slug ? "none" : "auto",
                      textDecoration: "none",
                      color: "inherit"
                    }}
                  >
                    <img
                      src={`${process.env.REACT_APP_WEB_URL}${item.thumbnail}`}
                      className="sidebar-img"
                      alt={item.title}
                    />
                    <div className="sidebar-news-info">
                      <h6>{item.title}</h6>
                      <p className="date">{formatDate(item.event_date)}</p>
                    </div>
                  </a>
                ))}
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default NewsDetails;
